import { useCampaignCode } from '@campaign-services/hooks/useCampaignCode'
import { type PartialCartItem } from '@cart-services/types'
import { DIALOG_PARTNER_AUTH } from '@cart-ui/DialogPartnerAuth/DialogPartnerAuth'
import { CountryCode, DISPLAY_STATUS, type DisplayStatusType, ProjectCode } from '@constants'
import { AnalyticsPlatforms, useAnalytics } from '@dni-analysis'
import { useDialog } from '@dni-hooks/useDialog'
import { useApiECommerceGetPaymentTransactionMutation } from '@hooks-api/eCommerceV2/useApiECommerceGetPaymentTransactionMutation'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { type OrderActionSettingResponse } from '@hooks-api/types/apiResponse'
import { type ECommerceOrder, type WpOrderStatus, type WpOrderType } from '@hooks-api/types/dataModel/eCommerceOrders'
import { OrderAction } from '@hooks-api/types/dataModel/wp'
import { useApiOrderActionSetting } from '@hooks-api/wp/useApiOrderActionSetting'
import { useApiUserProfile } from '@hooks-api/wp/useApiUserProfile'
import { ErrorFilled } from '@pets-ui/icons'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { getCountryCodeFromPathname, wordPressHost, wordPressNavigate } from '@utils'
import Cookies from 'js-cookie'
import { useCallback, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom-v5'
import { CANCEl_SUBSCRIPTION_DIALOG_KEY } from '../constants/dialogKey'
import { OrderContext } from '../useContextOrder'
import { useContextWpMyAccount } from '../useContextWpMyAccount'
import { transformOrderData } from '../utils/transformOrderDataDTO'
import { DialogAddToCartError } from './DialogAddToCartError'
import { DialogBuyAgain, type DialogBuyAgainProps, DIALOG_BUY_AGAIN } from './DialogBuyAgain'
import { DialogCancelSubscription, type DialogCancelSubscriptionProps } from './DialogCancelSubscription'
import { DialogConfirmCancelOrder } from './DialogConfirmCancelOrder'
import { DialogManualRefundRequired } from './DialogManualRefundRequired'
import { DialogReadyForShipmentCannotCancel } from './DialogReadyForShipmentCannotCancel'
import { DialogReviseDate } from './DialogReviseDate'
import { DialogReviseDeliveryAmount } from './DialogReviseDeliveryAmount'
import { DialogServerError } from './DialogServerError'
import { DialogUnsupportedSelfCancel } from './DialogUnsupportedSelfCancel'
import { OrderCard } from './OrderCard'
import { DIALOG_EDIT_PAYMENT_METHOD, DialogEditPaymentMethod, type DialogEditPaymentMethodProps } from './PaymentMethods/DialogEditPaymentMethod'
import { useCancelOrder } from './useCancelOrder'

interface OrderListProps {
  type: WpOrderType
  status: WpOrderStatus
  list: ECommerceOrder[]
  onRepayClick?: () => void
  onCancelOrderClick?: () => void
  siteId?: number
  focusedTransactionId?: string | null
}

export function OrderList(props: OrderListProps) {
  const { campaignCodeFromUrl } = useCampaignCode()
  const { environment, projectCode, campaignId } = useContextWpMyAccount()
  const countryCode = getCountryCodeFromPathname(window.location.pathname, CountryCode.TW) as CountryCode
  const { type, status, list, onRepayClick, onCancelOrderClick, siteId, focusedTransactionId } = props
  const transformedOrderData = transformOrderData(list)
  const focusedOrderRef = useRef<HTMLDivElement | null>(null)
  useEffect(function autoPageToFocusedOrder() {
    if (!focusedTransactionId || !focusedOrderRef.current) return
    focusedOrderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [focusedTransactionId])
  const isCampaignEnvironment = environment === 'campaign'
  const isMerchantEnvironment = environment === 'merchant'
  const isWordPressEnvironment = environment === 'wordPress'
  const { openDialog: openCancelSubscriptionDialog } = useDialog<DialogCancelSubscriptionProps>(CANCEl_SUBSCRIPTION_DIALOG_KEY)
  const { openDialog: openBuyAgainDialog } = useDialog<DialogBuyAgainProps>(DIALOG_BUY_AGAIN)
  const { trigger: triggerGetPaymentTransaction, isMutating: isMutatingGetPaymentTransaction } = useApiECommerceGetPaymentTransactionMutation()
  const { openSnackbar } = useSnackbar()
  const history = useHistory()
  const { sendPendingEvent } = useAnalytics({ defaultPlatforms: [AnalyticsPlatforms.GA4] })

  const { trigger: triggerOrderActionSetting } = useApiOrderActionSetting()
  /**
   * for 業務專站 / 2B cart
   */
  const { destructuredData: campaignMember } = useApiCampaignMember({ code: campaignCodeFromUrl })
  const { openDialog: openEditPaymentMethod } = useDialog<DialogEditPaymentMethodProps>(DIALOG_EDIT_PAYMENT_METHOD)
  const { destructuredData: userProfile } = useApiUserProfile({ site_id: siteId })
  const { openDialog: openDialogPartnerAuth } = useDialog(DIALOG_PARTNER_AUTH)
  const { handleCancelOrder } = useCancelOrder({ environment, countryCode, projectCode, campaignCodeFromUrl, onCancelOrderClick })
  const { sendEvent } = useAnalytics({ defaultPlatforms: [AnalyticsPlatforms.GA4] })
  const handleClickTransactionOrderDetail = (transactionId?: string | null) => {
    if (!transactionId) {
      console.error('transactionId is null or undefined!')
      return
    }
    if (isWordPressEnvironment) {
      wordPressNavigate(`/my-account/transaction/${transactionId}`)
    }
    if (isMerchantEnvironment) {
      history.push(`/merchant/${campaignCodeFromUrl}/my-account/order-detail/${transactionId}`)
    }
    if (isCampaignEnvironment) {
      history.push(`/campaigns/${campaignCodeFromUrl}/my-account/order-detail/${transactionId}`)
    }
  }
  /**
   * @deprecated
   * 舊版 woocommerce 的訂單動作
   */
  const handleLegacyWpOrderActions = (orderId: string, type: OrderAction, setting: OrderActionSettingResponse) => {
    if (type === OrderAction.CANCEL) {
      if (!setting.data.key || !setting.data.wp_nonce) return
      window.location.href = `${wordPressHost}/cart/?cancel_order=true&order=${setting.data.key}&order_id=${orderId}&redirect=${window.location.href}&_wpnonce=${setting.data.wp_nonce}`
    }
    if (type === OrderAction.REPEAT) {
      if (!setting.data.wp_nonce) return
      window.location.href = `${wordPressHost}/cart/?order_again=${orderId}&_wpnonce=${setting.data.wp_nonce}`
    }
    if (type === OrderAction.PAY) {
      if (!setting.data.key) return
      window.location.href = `${wordPressHost}/checkout/order-pay/${orderId}/?pay_for_order=${true}&key=${setting.data.key}`
    }
  }

  const handleOrderActions = async (type: OrderAction, paymentTransactionId: string, _orderId: number, _userId: string, transactionHash?: string, displayStatus?: DisplayStatusType) => {
    if (type === OrderAction.CANCEL) {
      await handleCancelOrder(paymentTransactionId, displayStatus)
      return
    }
    if (type === OrderAction.REPEAT) {
      try {
        const { data: { data } } = await triggerGetPaymentTransaction({ country_code: countryCode, id: paymentTransactionId })
        const orderItemsWithoutGiveawayAndAddon = data.child_orders
          ?.flatMap(childOrder => childOrder.order_items)
          .filter(item => !item.is_giveaway && !item.is_amount_giveaway)
          .filter(item => !item.is_addon_v2)
        const items: PartialCartItem[] = orderItemsWithoutGiveawayAndAddon?.map(item => ({
          cartItemId: item.variation_id || item.product_id,
          product_id: item.product_id,
          variation_id: Number(item.variation_id),
          quantity: item.quantity,
          sku: item.sku,
          delivery_class: item.delivery_class,
          product_limited: item.product_limited,
          project_code: projectCode,
          butler: {},
        })).filter(item => !!item.cartItemId) ?? []
        openBuyAgainDialog({ environment, countryCode, campaignId, items })
      }
      catch (error) {
        openSnackbar({
          message: '再次訂購失敗！請重新選購商品或聯繫客服人員',
          icon: <ErrorFilled color={colors.red600} />,
        })
        console.error(error)
      }
    }
    if (type === OrderAction.PAY) {
      /** for 業務專站 / 2B cart */
      if (isMerchantEnvironment) {
        onRepayClick?.()
        window.location.href = `/merchant/${campaignCodeFromUrl}/retry-payment/${paymentTransactionId}`
      }
      /** for 電子宅配單 / 2C cart */
      if (isCampaignEnvironment) {
        onRepayClick?.()
        if (transactionHash) {
          history.push(`/campaigns/${campaignCodeFromUrl}/payments?orderHash=${transactionHash}`)
        }
        else {
          openSnackbar({
            message: '立即失敗！請重新選購商品或聯繫客服人員',
            anchorOrigin: { horizontal: 'right', vertical: 'top' },
            icon: <ErrorFilled color={colors.red600} />,
          })
        }
      }
      if (isWordPressEnvironment) {
        /** for 官網 */
        window.location.href = `${wordPressHost}/checkout/order-pay/${paymentTransactionId}/`
      }
    }
  }

  const handleClickOrderAction = async (orderId: string, type: OrderAction, paymentTransactionId?: string | null, transactionHash?: string, displayStatus?: DisplayStatusType) => {
    /** for 業務專站 / 2B cart */
    if (isMerchantEnvironment || isCampaignEnvironment) {
      if (!paymentTransactionId) {
        console.error('[my account] paymentTransactionId not found')
        return
      }
      if (!campaignMember?.account) {
        console.error('[my account] campaign member account not found')
        return
      }
      handleOrderActions(type, paymentTransactionId, Number(orderId), campaignMember?.account, transactionHash, displayStatus)
    }
    if (isWordPressEnvironment) {
      if (userProfile?.is_partner) {
        openDialogPartnerAuth({ countryCode, projectCode })
        return
      }

      sendPendingEvent({
        eventName: 'purchase_again',
        eventParams: {},
      })

      const isTransactionOrder = !!paymentTransactionId
      const projectsWithTransaction = [ProjectCode.DCS, ProjectCode.HM, ProjectCode.LM]
      if (!isTransactionOrder && projectCode && projectsWithTransaction.includes(projectCode)) {
        if (type === OrderAction.REPEAT) {
          const toast = window.dniUtils.toast
          if (displayStatus === DISPLAY_STATUS.REFUNDED) {
            toast('error', '很抱歉，退款後的訂單不支援再次訂購，要麻煩毛爸媽重新選購商品呦！')
          }
          else {
            toast('error', '很抱歉，改版前的訂單不支援再次訂購，要麻煩毛爸媽重新選購商品呦！')
          }
        }
        if (type === OrderAction.PAY) {
          const toast = window.dniUtils.toast
          toast('error', '很抱歉，改版前的訂單不支援立即付款，要麻煩毛爸媽重新選購商品呦！')
        }
        if (type === OrderAction.CANCEL) {
          const toast = window.dniUtils.toast
          toast('error', '很抱歉，改版前的訂單不支援取消訂單，要麻煩毛爸媽重新選購商品呦！')
        }
        return
      }
      const userId = Cookies.get('user_id')
      if (!userId) return
      if (isTransactionOrder) {
        handleOrderActions(type, paymentTransactionId, Number(orderId), userId, transactionHash, displayStatus)
      }
      else {
        try {
          const { data } = await triggerOrderActionSetting({ order_id: orderId, user_id: userId, type })
          handleLegacyWpOrderActions(orderId, type, data)
        }
        catch (e) {
          console.error(e)
        }
      }
    }
  }

  const getOrderDetailUrl = useCallback((isTransaction: boolean, orderId: string, transactionId: string) => {
    if (!isTransaction) {
      return `${wordPressHost}/my-account/view-order/${orderId}/`
    }

    if (isWordPressEnvironment) {
      return `${wordPressHost}/my-account/transaction/${transactionId}`
    }

    if (isMerchantEnvironment) {
      return `/merchant/${campaignCodeFromUrl}/my-account/order-detail/${transactionId}`
    }

    return `/campaigns/${campaignCodeFromUrl}/my-account/order-detail/${transactionId}`
  }, [campaignCodeFromUrl, isMerchantEnvironment, isWordPressEnvironment])

  const handleOpenEditPaymentMethod = (orderId: string, fortuneOrderId: string, paymentTokenId: number | null) => {
    if (paymentTokenId === null) return
    openEditPaymentMethod({ orderId, fortuneOrderId, paymentTokenId, projectCode, userId: userProfile?.user_id })
  }

  const handleSendCancelSubscriptionEvent = () => {
    sendEvent({ eventName: 'open_subscription_popup', eventParams: {} })
  }

  return (
    <OrderContext.Provider value={{ orders: transformedOrderData }}>
      {transformedOrderData.map((order) => {
        const isFocusedOrder = !!focusedTransactionId && order.transactionId === focusedTransactionId
        return (
          <OrderCard
            key={order.orderId}
            order={order}
            type={type}
            status={status}
            isFocused={isFocusedOrder}
            focusedOrderRef={isFocusedOrder ? focusedOrderRef : undefined}
            isMutatingGetPaymentTransaction={isMutatingGetPaymentTransaction}
            onClickOrderAction={handleClickOrderAction}
            onClickTransactionOrderDetail={handleClickTransactionOrderDetail}
            getOrderDetailUrl={getOrderDetailUrl}
            onOpenCancelSubscriptionDialog={openCancelSubscriptionDialog}
            onOpenEditPaymentMethod={handleOpenEditPaymentMethod}
            onSendCancelSubscriptionEvent={handleSendCancelSubscriptionEvent}
          />
        )
      })}
      <DialogReviseDate />
      <DialogReviseDeliveryAmount />
      <DialogCancelSubscription />
      <DialogServerError />
      <DialogBuyAgain />
      <DialogAddToCartError />
      <DialogEditPaymentMethod />
      <DialogReadyForShipmentCannotCancel />
      <DialogManualRefundRequired />
      <DialogConfirmCancelOrder />
      <DialogUnsupportedSelfCancel />
    </OrderContext.Provider>
  )
}
